import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Bell, Check, X, CheckCheck } from "lucide-react";

/**
 * NotificationBell — small drop-in bell with unread badge + panel of recent
 * notifications. Polls every 60s while mounted.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 15 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch { /* not logged in — ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      load();
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch { /* ignore */ }
  };

  const remove = async (nid, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await api.delete(`/notifications/${nid}`);
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg border-2 border-transparent hover:border-slate-300 hover:bg-white transition-colors"
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "Notifications"}
        data-testid="notification-bell"
      >
        <Bell size={18} strokeWidth={2.5} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] flex flex-col brut-card bg-white z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b-2 border-slate-100 bg-amber-50">
            <div className="font-heading font-bold text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck size={11} strokeWidth={3} /> Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400 italic">
                No notifications yet. When someone forks or grades your models, you'll see it here.
              </div>
            ) : (
              items.map((n) => (
                <NotifRow key={n.notification_id} n={n} onRead={markRead} onRemove={remove} onClose={() => setOpen(false)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, onRead, onRemove, onClose }) {
  const Inner = ({ children }) => n.link ? (
    <Link to={n.link} onClick={() => { onRead(n.notification_id); onClose(); }} className="block">{children}</Link>
  ) : (
    <div onClick={() => onRead(n.notification_id)} className="block cursor-pointer">{children}</div>
  );
  return (
    <div className="border-b-2 border-slate-100 last:border-b-0 group relative hover:bg-amber-50 transition-colors" data-testid={`notif-${n.notification_id}`}>
      <Inner>
        <div className="p-3 pr-8">
          <div className="flex items-start gap-2">
            {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
            <div className={`flex-1 min-w-0 ${n.read ? "opacity-60" : ""}`}>
              <div className="font-heading font-bold text-sm leading-tight">{n.title}</div>
              {n.body && <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>}
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400 mt-1">
                {timeAgo(n.created_at)}
              </div>
            </div>
          </div>
        </div>
      </Inner>
      <button
        onClick={(e) => onRemove(n.notification_id, e)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
        title="Dismiss"
        data-testid={`notif-remove-${n.notification_id}`}
      >
        <X size={12} strokeWidth={3} />
      </button>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
